import dynamic from 'next/dynamic';

import { CategoryCarouselProps, Control } from 'src/components/blocks/CategoryCarouselBlock';
import { fetchCategoriesWithProducts } from 'src/services/catalog';
import { trackCarouselNavigation } from 'src/tracking/common/carousel';
import { trackCategoryClick, trackImpression } from 'src/tracking/components/categoryCarousel';
import { ContentBlockRenderer } from 'src/types/Block';
import { PlacementContext } from 'src/types/PlacementContext';
import { AsyncTransformer } from 'src/types/Transformer';
import { categoryContentToModel } from './contentToModel';
import {
  CategoryCarouselSerializedData,
  ContentCategory,
  ContentCategoryCarouselSection,
} from './types';
import { validateCategoryCarousel } from './validation';

const getCategoryProductPairs = (categories: ContentCategory[]) =>
  categories.map((item) => ({
    categoryId: item.category_id,
    productCode: item.product_code,
  }));

const getCategoryProductData = async (categories: ContentCategory[]) => {
  const categoryProductPairs = getCategoryProductPairs(categories);
  let categoryProductData = null;

  try {
    categoryProductData = await fetchCategoriesWithProducts(categoryProductPairs);
  } catch (err) {
    console.error('Error loading the category carousel', err);
  }

  return categoryProductData;
};

/* istanbul ignore next */
export const renderer: ContentBlockRenderer<CategoryCarouselSerializedData, CategoryCarouselProps> =
  {
    Component: dynamic<CategoryCarouselProps>(() =>
      import('src/components/blocks/CategoryCarouselBlock').then(
        (mod) => mod.CategoryCarouselBlock,
      ),
    ),
    getProps: (serializedData) => {
      const { trackingTitle, trackingPosition, categories, ...restOfSerializedData } =
        serializedData;
      const dataForTracking: PlacementContext = {
        trackingPosition,
        trackingTitle,
        placementContentType: 'Category carousel',
      };

      return {
        ...restOfSerializedData,
        categories,
        onVisible: () => {
          if (categories.length > 0) {
            trackImpression({
              ...dataForTracking,
              numberOfItems: categories.length,
            });
          }
        },
        onCategoryClick: (e, { index }) => {
          trackCategoryClick(e, {
            ...dataForTracking,
            category: categories[index],
            categoryIndex: index,
            numberOfItems: categories.length,
          });
        },
        onNavigation: (swiped: boolean, control: Control) => {
          const direction = control === 'next' ? 'next' : 'previous';

          trackCarouselNavigation(swiped, direction, {
            ...dataForTracking,
            numberOfItems: categories.length,
          });
        },
      };
    },
  };

export const transformer: AsyncTransformer<
  ContentCategoryCarouselSection,
  CategoryCarouselSerializedData,
  CategoryCarouselProps
> = async ({ _metadata, carousel_title, categories, tracking_title }) => {
  const categoryProductData = await getCategoryProductData(categories);

  return {
    uid: _metadata.uid,
    renderer,
    serializedData: {
      title: carousel_title,
      categories: categoryProductData
        ? categoryContentToModel(categoryProductData, categories)
        : [],
      trackingTitle: tracking_title,
    },
    errors: validateCategoryCarousel(categories, categoryProductData),
  };
};
